import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, FileText, Trash2, Loader2, UserCircle } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';

export default function FileRepositoryCard() {
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const { data: currentUser } = useQuery({
    queryKey: ['me'],
    queryFn: () => base44.auth.me()
  });

  const { data: officers = [] } = useQuery({
    queryKey: ['officers'],
    queryFn: () => base44.entities.OfficerProfile.list()
  });

  const { data: files = [], isLoading, error } = useQuery({
    queryKey: ['fileRepositoryFiles'],
    queryFn: () => base44.entities.FileRepositoryItem.list()
  });

  const uploadFileMutation = useMutation({
    mutationFn: async (file) => {
      if (!currentUser || !currentUser.email) {
        throw new Error('User not authenticated.');
      }
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      return base44.entities.FileRepositoryItem.create({
        file_name: file.name,
        description: `Uploaded by ${currentUser.email}`,
        file_url: file_url,
        uploaded_by: currentUser.email,
        file_size: file.size,
        file_type: file.type
      });
    },
    onMutate: () => setUploading(true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fileRepositoryFiles'] });
      setSelectedFile(null);
      setUploading(false);
    },
    onError: (err) => {
      console.error('Error uploading file:', err);
      alert(`Error uploading file: ${err.message}`);
      setUploading(false);
    }
  });

  const deleteFileMutation = useMutation({
    mutationFn: (fileId) => base44.entities.FileRepositoryItem.delete(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fileRepositoryFiles'] });
    },
    onError: (err) => {
      console.error('Error deleting file:', err);
      alert(`Error deleting file: ${err.message}`);
    }
  });

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      uploadFileMutation.mutate(selectedFile);
    }
  };

  const getOfficerName = (email) => {
    const officer = officers.find((o) => o.user_email === email);
    return officer ? officer.callsign : email;
  };

  return (
    <Card className="glass-panel border-none border-l-4 border-l-teal-500">
      <CardHeader>
        <CardTitle className="text-slate-600 font-semibold tracking-tight leading-none flex items-center gap-2">
          <FileText className="w-5 h-5 text-teal-400" />
          File Repository
        </CardTitle>
        <p className="text-sm text-slate-400">Upload, view, and manage important documents.</p>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col md:flex-row items-center gap-3 mb-4">
          <input data-testid="arch-filerepositorycard-input" type="file" onChange={handleFileChange} className="block w-full text-sm text-slate-500
            file:mr-4 file:py-2 file:px-4
            file:rounded-full file:border-0
            file:text-sm file:font-semibold
            file:bg-indigo-50 file:text-indigo-700
            hover:file:bg-indigo-100
          " />












          <Button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="w-full md:w-auto bg-indigo-600 hover:bg-indigo-700 text-white">

            {uploading ?
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> :

            <Upload className="w-4 h-4 mr-2" />
            }
            {uploading ? 'Uploading...' : 'Upload File'}
          </Button>
        </div>

        {isLoading && <p className="text-slate-500">Loading files...</p>}
        {error && <p className="text-red-500">Error loading files: {error.message}</p>}

        {files.length === 0 && !isLoading && !error &&
        <div className="py-8 text-center border border-dashed border-slate-800 rounded-xl">
            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500">No files uploaded yet.</p>
          </div>
        }

        {files.length > 0 &&
        <div className="space-y-3">
            {files.map((file) =>
          <div key={file.id} className="bg-slate-50 text-slate-700 p-3 rounded-lg flex items-center justify-between border border-slate-700">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-teal-400" />
                  <div>
                    <a
                  href={file.file_url}
                  target="_blank"
                  rel="noopener noreferrer" className="text-slate-600 font-medium hover:text-teal-400 transition-colors">


                      {file.file_name}
                    </a>
                    <div className="flex items-center text-xs text-slate-500 mt-1">
                      <UserCircle className="w-3 h-3 mr-1" />
                      <span>{getOfficerName(file.uploaded_by)}</span>
                      <span className="mx-1">•</span>
                      <span>{moment(file.created_date).format('MMM D, YYYY h:mm A')}</span>
                    </div>
                  </div>
                </div>
                <Button
              size="icon"
              variant="ghost"
              onClick={() => deleteFileMutation.mutate(file.id)}
              className="h-8 w-8 text-slate-500 hover:text-red-400">

                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
          )}
          </div>
        }
      </CardContent>
    </Card>);

}